import { useMemo, useState } from 'react'

import { TextareaWithSubmit } from '@/components/composer/TextareaWithSubmit'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { type EmailTemplate, useEmailTemplates } from '../../models/emailTemplate'
import { useSendSms } from '../../models/telephony'
import { markdownToPlainText } from '../../utils/markdownToPlainText'
import { fillDriverName } from '../../utils/templateVariables'
import { EmailTemplateSelectItems } from '../EmailTemplateSelectItems'
import { type AircallNumber } from './types'

interface Props {
  leadId: string
  recipient: AircallNumber
  contactName: string
  onSent: () => void
  onCancel: () => void
}

const NO_TEMPLATE = '__none__'
const SMS_SEGMENT_LEN = 160

function firstName(contactName: string): string {
  return contactName.trim().split(/\s+/)[0] || 'there'
}

function renderTemplateForSms(template: EmailTemplate, driverName: string): string {
  return markdownToPlainText(fillDriverName(template.body, driverName))
}

export function SmsComposer({ leadId, recipient, contactName, onSent, onCancel }: Props) {
  const [body, setBody] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(NO_TEMPLATE)
  const sendMutation = useSendSms(leadId)
  const { data: templates = [] } = useEmailTemplates()

  const driverName = useMemo(() => firstName(contactName), [contactName])
  const segments = body.length === 0 ? 0 : Math.ceil(body.length / SMS_SEGMENT_LEN)

  function handleTemplateChange(id: string) {
    setSelectedTemplateId(id)
    if (id === NO_TEMPLATE) {
      setBody('')
      return
    }
    const template = templates.find((t) => t.id === id)
    if (template) setBody(renderTemplateForSms(template, driverName))
  }

  function handleSubmit(message: string) {
    sendMutation.mutate(
      { to: recipient.value, body: message, leadId },
      {
        onSuccess: () => {
          setBody('')
          setSelectedTemplateId(NO_TEMPLATE)
          onSent()
        },
      }
    )
  }

  const errorMessage = sendMutation.isError
    ? ((sendMutation.error as { response?: { data?: { error?: string } } }).response?.data?.error ??
      'Send failed')
    : null

  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm">
        Texting {recipient.label.toLowerCase()} · <span className="font-medium">{recipient.value}</span>
      </div>
      {templates.length > 0 && (
        <Select
          value={selectedTemplateId}
          onValueChange={handleTemplateChange}
          disabled={sendMutation.isPending}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="No template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_TEMPLATE}>No template</SelectItem>
            <EmailTemplateSelectItems templates={templates} />
          </SelectContent>
        </Select>
      )}
      <TextareaWithSubmit
        value={body}
        onValueChange={setBody}
        placeholder="Write your message…"
        onSubmitMessage={({ message }) => handleSubmit(message)}
        buttonProps={{ disabled: sendMutation.isPending }}
        addonChildren={
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        }
        leadingHint={
          <>
            {body.length} chars
            {segments > 0 && (
              <>
                {' · '}
                {segments} segment{segments === 1 ? '' : 's'}
              </>
            )}
          </>
        }
      />
      {errorMessage && <p className="mt-1 text-xs text-red-600">{errorMessage}</p>}
    </div>
  )
}
